import { createNumberInputGuards } from "../numberInput";

const { sanitise, toCssNumber } = createNumberInputGuards({ allowNegative: false });

// The bespoke selects (font, block format, style, size) all render the same markup, so the
// aria-label is the only thing that tells the size one apart.
const LABEL_SELECTOR = '.tox-tbtn--select[aria-label="Font sizes"] .tox-tbtn__select-label';

// The toolbar mirrors whatever unit the content carries, so the old one is stripped before
// editing and px, the only unit this editor offers, is written back.
const UNIT_PATTERN = /[a-z%]+$/i;
const UNIT = "px";
const MIN_PX = 4;
const MAX_PX = 400;

const setRange = (label, collapseToEnd) => {
  const range = document.createRange();
  const selection = window.getSelection();

  range.selectNodeContents(label);

  if (collapseToEnd) range.collapse(false);

  selection.removeAllRanges();
  selection.addRange(range);
};

const selectLabel = label => setRange(label, false);
const caretToEnd = label => setRange(label, true);

const clampSize = value => String(Math.min(Math.max(Number(value), MIN_PX), MAX_PX));

/**
 * Makes the font size dropdown's label typable, so sizes outside `fontsize_formats` can be
 * entered straight in the toolbar. TinyMCE only grew an editable size field in 6
 * (`fontsizeinput`); in 5 the label is a plain span inside the menu button, so the caret
 * reaches it only if the click stops before the toolbar opens the menu.
 */
const makeFontSizeEditable = editor => {
  const label = editor.getContainer()?.querySelector(LABEL_SELECTOR);

  if (!label) return;

  // What the toolbar showed before the edit, so a discarded one can be put back.
  let committed = label.textContent;

  label.contentEditable = "true";
  label.spellcheck = false;

  label.addEventListener(
    "mousedown",
    event => {
      event.stopPropagation();
      // The caret is placed by hand instead: a native drag-select would collapse the
      // select-all below on mouseup.
      event.preventDefault();
      label.focus();
    },
    true,
  );

  label.addEventListener("click", event => event.stopPropagation(), true);

  label.addEventListener("focus", () => {
    committed = label.textContent;
    label.textContent = committed.replace(UNIT_PATTERN, "");
    selectLabel(label);
  });

  label.addEventListener("input", () => {
    const sanitised = sanitise(label.textContent);

    if (sanitised !== label.textContent) {
      label.textContent = sanitised;
      caretToEnd(label);
    }
  });

  // The label sits inside the menu button, which reads Enter and Space as "open the preset
  // list"; while a size is being typed none of those shortcuts should reach it.
  label.addEventListener("keydown", event => {
    event.stopPropagation();

    if (event.key === "Enter") {
      event.preventDefault();
      label.blur();
    }

    if (event.key === "Escape") {
      event.preventDefault();
      label.textContent = committed;
      label.blur();
    }
  });

  label.addEventListener("blur", () => {
    // Re-validated here because pasting bypasses the per-keystroke filter
    const value = toCssNumber(label.textContent);
    const size = value === "" ? committed : `${clampSize(value)}${UNIT}`;

    label.textContent = size;

    // Merely visiting the field must not touch the document: applying an unchanged size
    // would wrap the selection in a font-size span it never had.
    if (size === committed) return;

    // execCommand focuses the editor first, which restores the selection the toolbar took
    // focus from, so the size lands on the text that was selected.
    editor.execCommand("FontSize", false, size);
  });
};

export { makeFontSizeEditable };
